'use client';

import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import type { TableColumnsDisplayConfig } from '../shared/table-columns-display';
import type { AccountsTableColumn } from './utils/accounts-columns';
import { TableColumnsDisplay } from '../shared/table-columns-display';
import {
  DEFAULT_ACCOUNTS_TABLE_COLUMNS,
  getDefaultAccountsColumns,
  setDefaultAccountsColumns,
} from './utils/accounts-columns';

type DisplayOption = Exclude<AccountsTableColumn, 'Account Name'>;

// Each billing/orion column is its own toggle so advisors add them one at a
// time rather than enabling a bundle of three at once.
const BILLING_OPTIONS: readonly DisplayOption[] = [
  'Orion Fee Schedule',
  'Orion Frequency',
  'Bill Style',
  'Orion Pay Method',
  'Start Date',
  'Recurring Adjustment Start Date',
  'Recurring Adjustment End Date',
  'Recurring Adjustment Schedule',
  'Fee Type',
  'Amount Type',
  'Flat Fee Amount',
  'Minimum Fee',
] as const;

const BASE_DISPLAY_OPTIONS_WITHOUT_CASH_MM: readonly DisplayOption[] = [
  'Balance',
  'Household',
  'Primary Advisor',
  'Strategy',
  ...BILLING_OPTIONS,
] as const;
const BASE_DISPLAY_OPTIONS_WITH_CASH_MM: readonly DisplayOption[] = [
  'Balance',
  'Household',
  'Primary Advisor',
  'Strategy',
  'Cash & Money Market',
  ...BILLING_OPTIONS,
] as const;

function createColumnsToDisplayOptions(orderedOptions: readonly DisplayOption[]) {
  return (columns: readonly AccountsTableColumn[]): DisplayOption[] => {
    const set = new Set(columns);
    return orderedOptions.filter((opt) => set.has(opt));
  };
}

function createDisplayOptionsToColumns(orderedOptions: readonly DisplayOption[]) {
  return (options: readonly DisplayOption[]): AccountsTableColumn[] => {
    const ordered = orderedOptions.filter((opt) => options.includes(opt));
    return ['Account Name', ...ordered];
  };
}

function ensureRequiredColumns(columns: readonly AccountsTableColumn[]): AccountsTableColumn[] {
  if (!columns.includes('Account Name')) {
    return ['Account Name', ...columns];
  }
  return [...columns];
}

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly anchorEl: HTMLElement | null;
  readonly selectedColumns: readonly AccountsTableColumn[];
  readonly onColumnsChange: (columns: AccountsTableColumn[]) => void;
};

export function AccountsColumnsDisplay({ open, onClose, anchorEl, selectedColumns, onColumnsChange }: Props) {
  const { flagCashDashboard: cashDashboardEnabled } = useFlags();

  const orderedDisplayOptions = useMemo(
    () => (cashDashboardEnabled === true ? BASE_DISPLAY_OPTIONS_WITH_CASH_MM : BASE_DISPLAY_OPTIONS_WITHOUT_CASH_MM),
    [cashDashboardEnabled],
  );

  const config: TableColumnsDisplayConfig<AccountsTableColumn, DisplayOption> = useMemo(
    () => ({
      displayOptions: orderedDisplayOptions,
      displayOptionToColumns: createDisplayOptionsToColumns(orderedDisplayOptions),
      columnsToDisplayOptions: createColumnsToDisplayOptions(orderedDisplayOptions),
      requiredColumns: ['Account Name'] as const,
      ensureRequiredColumns,
      title: 'Display columns',
      countLabel: 'Columns',
      includeRequiredInCount: true, // Accounts includes Account Name in the count
      getDefaultColumns: getDefaultAccountsColumns,
      getHardcodedDefaults: () => [...DEFAULT_ACCOUNTS_TABLE_COLUMNS],
      setDefaultColumns: setDefaultAccountsColumns,
      selectedColumns,
      onColumnsChange,
    }),
    [orderedDisplayOptions, selectedColumns, onColumnsChange],
  );

  return <TableColumnsDisplay open={open} onClose={onClose} anchorEl={anchorEl} config={config} />;
}
